import React, { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import type { MessageStatus as MessageStatusType } from '../../types/message';

interface MessageStatusProps {
  dateTime?: string;
  status?: MessageStatusType;
  isOutbound: boolean;
  sendError?: string;
}

const StatusIcon: React.FC<{ status: MessageStatusType }> = ({ status }) => {
  switch (status) {
    case 'sending':
      return (
        <div className="w-3.5 h-3.5">
          <svg className="animate-spin text-graphite" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      );
    case 'sent':
      return (
        <svg className="w-3.5 h-3.5 text-graphite" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'delivered':
      return (
        <svg className="w-4 h-3.5 text-graphite" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2 13l4 4L16 7" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 13l4 4L22 7" />
        </svg>
      );
    case 'read':
      return (
        <svg className="w-4 h-3.5 text-deep-sky" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2 13l4 4L16 7" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 13l4 4L22 7" />
        </svg>
      );
    case 'failed':
      return (
        <svg className="w-3.5 h-3.5 text-brick" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    default:
      return (
        <svg className="w-3.5 h-3.5 text-graphite" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
  }
};

export const MessageStatus: React.FC<MessageStatusProps> = ({
  dateTime,
  status,
  isOutbound,
  sendError,
}) => {
  const [showError, setShowError] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showError) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        setShowError(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showError]);

  const formattedTime = dateTime
    ? format(new Date(dateTime), 'h:mm a')
    : '';

  const isFailed = status === 'failed';

  return (
    <div className="relative" ref={tooltipRef}>
      <div className="flex items-center justify-end gap-1 mt-1">
        {formattedTime && (
          <span className="text-[11px] text-graphite">{formattedTime}</span>
        )}
        {isOutbound && status && (
          <button
            type="button"
            className={isFailed && sendError ? 'cursor-pointer' : 'cursor-default'}
            onClick={() => isFailed && sendError && setShowError(!showError)}
          >
            <StatusIcon status={status} />
          </button>
        )}
      </div>

      {/* Error tooltip */}
      {showError && sendError && (
        <div className="absolute bottom-full right-0 mb-1 z-10 max-w-[260px]">
          <div className="bg-brick/10 border border-brick/30 rounded-lg px-3 py-2 shadow-lg text-xs text-brick">
            {sendError}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageStatus;
